import { useState } from 'react';
import { useVouchers } from '@/hooks/useVouchers';
import { useProducts } from '@/hooks/useProducts';
import { useOutlets } from '@/hooks/useOutlets';
import { api } from '@/lib/api';
import type { Voucher } from '@photobooth/types';

type FormData = {
  code: string;
  name: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  quota: number;
  maxUsagePerCustomer: number | null;
  productIds: string[];
  outletIds: string[];
  validFrom: string;
  validUntil: string;
  isActive: boolean;
};

function VoucherForm({
  data,
  onChange,
  products,
  outlets,
  idPrefix = 'voucher',
}: {
  data: FormData;
  onChange: (d: FormData) => void;
  products: { productId: string; name: string }[];
  outlets: { outletId: string; name: string }[];
  idPrefix?: string;
}) {
  const isActiveId = `${idPrefix}-isActive`;
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-text mb-1">Code *</label>
        <input
          type="text"
          required
          value={data.code}
          onChange={(e) => onChange({ ...data, code: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="SUMMER20"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-text mb-1">Name</label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="Summer discount"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text mb-1">Discount Type</label>
          <select
            value={data.discountType}
            onChange={(e) => onChange({ ...data, discountType: e.target.value as 'percentage' | 'fixed' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-1">
            Discount Value {data.discountType === 'percentage' ? '(%)' : '(Rp)'}
          </label>
          <input
            type="number"
            required
            min={0}
            step={data.discountType === 'percentage' ? 1 : 1000}
            value={data.discountValue || ''}
            onChange={(e) => onChange({ ...data, discountValue: Number(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text mb-1">Quota</label>
          <input
            type="number"
            min={0}
            value={data.quota}
            onChange={(e) => onChange({ ...data, quota: Number(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-1">Max Uses Per Customer</label>
          <input
            type="number"
            min={0}
            placeholder="Unlimited"
            value={data.maxUsagePerCustomer ?? ''}
            onChange={(e) =>
              onChange({ ...data, maxUsagePerCustomer: e.target.value ? Number(e.target.value) : null })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-text mb-1">Products (leave empty for all)</label>
        <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
          {products.map((p) => (
            <label key={p.productId} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.productIds.includes(p.productId)}
                onChange={(e) => {
                  const newIds = e.target.checked
                    ? [...data.productIds, p.productId]
                    : data.productIds.filter((id) => id !== p.productId);
                  onChange({ ...data, productIds: newIds });
                }}
                className="rounded"
              />
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-text mb-1">Outlets (leave empty for all)</label>
        <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
          {outlets.map((o) => (
            <label key={o.outletId} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.outletIds.includes(o.outletId)}
                onChange={(e) => {
                  const newIds = e.target.checked
                    ? [...data.outletIds, o.outletId]
                    : data.outletIds.filter((id) => id !== o.outletId);
                  onChange({ ...data, outletIds: newIds });
                }}
                className="rounded"
              />
              <span>{o.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text mb-1">Valid From</label>
          <input
            type="datetime-local"
            value={data.validFrom}
            onChange={(e) => onChange({ ...data, validFrom: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-1">Valid Until</label>
          <input
            type="datetime-local"
            value={data.validUntil}
            onChange={(e) => onChange({ ...data, validUntil: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={isActiveId}
          checked={data.isActive}
          onChange={(e) => onChange({ ...data, isActive: e.target.checked })}
          className="rounded"
        />
        <label htmlFor={isActiveId}>Active</label>
      </div>
    </div>
  );
}

export function VouchersPage() {
  const { vouchers, isLoading, error, refetch } = useVouchers();
  const { products } = useProducts();
  const { outlets } = useOutlets();
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: 0,
    quota: 0,
    maxUsagePerCustomer: null as number | null,
    productIds: [] as string[],
    outletIds: [] as string[],
    validFrom: '',
    validUntil: '',
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState(formData);

  const filteredVouchers = search.trim()
    ? vouchers.filter(
        (v) =>
          v.code.toLowerCase().includes(search.toLowerCase()) ||
          (v.name?.toLowerCase().includes(search.toLowerCase()) ?? false)
      )
    : vouchers;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.vouchers.create({
        code: formData.code.trim(),
        name: formData.name.trim() || undefined,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        quota: formData.quota,
        maxUsagePerCustomer: formData.maxUsagePerCustomer,
        productIds: formData.productIds.length ? formData.productIds : null,
        outletIds: formData.outletIds.length ? formData.outletIds : null,
        validFrom: formData.validFrom ? `${formData.validFrom}:00` : null,
        validUntil: formData.validUntil ? `${formData.validUntil}:00` : null,
        isActive: formData.isActive,
      });
      setFormData({
        code: '',
        name: '',
        discountType: 'percentage',
        discountValue: 0,
        quota: 0,
        maxUsagePerCustomer: null,
        productIds: [],
        outletIds: [],
        validFrom: '',
        validUntil: '',
        isActive: true,
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create voucher');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (v: Voucher) => {
    setEditingId(v.id);
    setEditFormData({
      code: v.code,
      name: v.name ?? '',
      discountType: v.discountType,
      discountValue: v.discountValue,
      quota: v.quota,
      maxUsagePerCustomer: v.maxUsagePerCustomer ?? null,
      productIds: v.productIds ?? [],
      outletIds: v.outletIds ?? [],
      validFrom: v.validFrom ? new Date(v.validFrom).toISOString().slice(0, 19) : '',
      validUntil: v.validUntil ? new Date(v.validUntil).toISOString().slice(0, 19) : '',
      isActive: v.isActive,
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setIsSubmitting(true);
    try {
      const response = await api.vouchers.update(editingId, {
        code: editFormData.code.trim(),
        name: editFormData.name.trim() || null,
        discountType: editFormData.discountType,
        discountValue: editFormData.discountValue,
        quota: editFormData.quota,
        maxUsagePerCustomer: editFormData.maxUsagePerCustomer,
        productIds: editFormData.productIds.length ? editFormData.productIds : null,
        outletIds: editFormData.outletIds.length ? editFormData.outletIds : null,
        validFrom: editFormData.validFrom ? `${editFormData.validFrom}:00` : null,
        validUntil: editFormData.validUntil ? `${editFormData.validUntil}:00` : null,
        isActive: editFormData.isActive,
      });
      if (response.success) {
        setEditingId(null);
        refetch();
      } else {
        alert('Failed to update voucher');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update voucher');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deactivate this voucher?')) return;
    try {
      await api.vouchers.delete(id);
      refetch();
    } catch (err) {
      alert('Failed to deactivate voucher');
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Vouchers</h1>
          <p className="text-text-muted mt-1">Manage discount codes</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ New Voucher'}
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
      )}

      {showForm && (
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-text mb-4">Create Voucher</h2>
          <form onSubmit={handleSubmit}>
            <VoucherForm data={formData} onChange={setFormData} products={products} outlets={outlets} idPrefix="create" />
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-4 w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Voucher'}
            </button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVouchers.map((v) => (
          <div key={v.id} className="bg-surface rounded-lg shadow p-6">
            {editingId === v.id ? (
              <form onSubmit={handleUpdate}>
                <VoucherForm data={editFormData} onChange={setEditFormData} products={products} outlets={outlets} idPrefix={`edit-${editingId}`} />
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-text">{v.code}</h3>
                  <span
                    className={`px-2 py-1 rounded text-xs ${v.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {v.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {v.name && <p className="text-sm text-text-muted mb-2">{v.name}</p>}
                <p className="text-sm mb-2">
                  {v.discountType === 'percentage'
                    ? `${v.discountValue}% off`
                    : `Rp ${v.discountValue.toLocaleString('id-ID')} off`}
                </p>
                <p className="text-sm text-text-muted mb-4">
                  Used {v.usedCount}/{v.quota || '∞'}
                  {v.maxUsagePerCustomer != null && ` (max ${v.maxUsagePerCustomer}/customer)`}
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={() => handleEditClick(v)}
                    className="text-primary hover:text-blue-700 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(v.id)}
                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    Deactivate
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
